import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import { Link, useParams, useHistory } from "react-router-dom";
import { PageHeader, Button, notification } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { useInjectReducer, useInjectSaga } from "redux-injectors";
import { yupResolver } from "@hookform/resolvers/yup";
import { sliceKey, reducer, actions } from "./slice";
import { labEditSaga } from "./saga";
import * as selectors from "./selectors";
import AddLabForm from "../../../../forms/AddLabForm";
import routes from "../../../../../routes";
import Loader from "../../../../components/Loader";
import * as yup from "yup";
import { reverse } from "named-urls";

const schema = yup.object().shape({
  name: yup
    .string()
    .required()
    .matches(/^(?!\s+$).+/, "Please provide valid values for name")
    .label("Registered Name"),

  operatingStates: yup.array().min(1).required().label("Operating State"),
  address: yup.object().shape({
    addressLine1: yup
      .string()
      .required()
      .matches(/^(?!\s+$).+/, "Please provide valid values in address field")
      .label("Address Line 1"),
    addressLine2: yup.string().label("Address Line 2"),
    country: yup.string().required().label("Country"),
    state: yup.string().required().label("State"),
    city: yup.string().required().label("City"),
    pincode: yup
      .string()
      .required()
      .matches(/^[0-9]+$/, "Please enter valid pincode")
      .min(6)
      .max(6)
      .label("Pincode"),
  }),
  user: yup.object().shape({
    firstName: yup
      .string()
      .required()
      .matches(/^(?!\s+$).+/, "Please provide valid value in First Name field")
      .label("First Name"),
    lastName: yup.string().label("Last Name"),
    email: yup.string().email().required().label("Business Email"),
    phone: yup
      .string()
      .required()
      .matches(/^[0-9]+$/, "Please enter valid contact number")
      .min(10)
      .max(10)
      .label("Contact Phone Number"),
  }),
  testingScope: yup.mixed().label("Testing Scope"),
  contract: yup.mixed().label("Contract"),
});

const EditLabPartner = () => {
  useInjectReducer({ key: sliceKey, reducer: reducer });
  useInjectSaga({ key: sliceKey, saga: labEditSaga });
  const dispatch = useDispatch();
  const { labId } = useParams();
  const history = useHistory();

  // effects
  useEffect(() => {
    dispatch(actions.fetchLabDetails({ id: labId }));
    dispatch(actions.fetchCountries());
    return () => {
      dispatch(actions.initializeState());
    };
  }, [dispatch, labId]);

  // selectors
  const labData = useSelector(selectors.selectLabDetails);
  const countries = useSelector(selectors.selectCountries);
  const states = useSelector(selectors.selectStates);
  const cities = useSelector(selectors.selectCities);
  const loading = useSelector(selectors.selectLoading);
  const error = useSelector(selectors.selectError);

  const {
    control,
    handleSubmit,
    watch,
    reset,
    formState: { errors },
    setValue,
  } = useForm({
    defaultValues: {
      name: "",
      testingScope: "",
      contract: "",
      operatingStates: [],
      address: {
        addressLine1: "",
        addressLine2: "",
        city: "",
        state: "",
        country: "",
        pincode: "",
      },
      user: {
        firstName: "",
        lastName: "",
        email: "",
        password: "",
        phone: "",
      },
    },
    resolver: yupResolver(schema),
  });

  const selectedCountry = watch("address.country");
  const selectedState = watch("address.state");

  useEffect(() => {
    if (selectedState) {
      // no operation for reset
    } else {
      const data = {
        address: labData?.address,
        contract: labData?.contract,
        name: labData?.name,
        operatingStates: labData?.operatingStates,
        user: labData?.poc,
      };
      console.log(data);
      reset(data);
    }
  }, [reset, labData, selectedState]);

  useEffect(() => {
    if (error && error !== "") {
      notification.error({
        message: "Error Updating Lab",
        description: error,
      });
      setTimeout(() => {
        dispatch(actions.initializeError());
      }, 1500);
    }
  }, [error, dispatch]);

  useEffect(() => {
    if (selectedCountry)
      dispatch(actions.fetchStates({ countryId: selectedCountry }));
  }, [selectedCountry, dispatch]);

  useEffect(() => {
    if (selectedState)
      dispatch(actions.fetchCities({ stateId: selectedState }));
  }, [selectedState, dispatch]);

  const handleSuccess = () => {
    notification.success({
      message: "Lab got edited successfully",
    });
    history.push(reverse(routes.backOffice.labPartners.list));
  };

  const handleFailure = () => {
    console.log("An error occured");
  };

  const onSubmit = (data) => {
    const payload = {
      onSuccess: handleSuccess,
      id: labId,
      data,
      onFailure: handleFailure,
    };
    console.log(payload);
    dispatch(actions.updateLab(payload));
  };

  return (
    <div style={{ height: "100%" }}>
      <PageHeader
        ghost={false}
        title="Edit Lab Partner Details"
        subTitle={labData?.name}
        extra={[
          <Button key="1">
            <Link to={routes.backOffice.labPartners.list}>Cancel</Link>
          </Button>,
          <Button
            disabled={loading}
            loading={loading}
            key="2"
            type="primary"
            onClick={handleSubmit(onSubmit)}
          >
            Update
          </Button>,
        ]}
      />
      <div
        style={{
          height: "calc(100vh - 137px)",
          overflowY: "auto",
          padding: 24,
        }}
      >
        {loading ? (
          <Loader />
        ) : (
          <AddLabForm
            options={{
              states,
              cities,
              countries,
            }}
            control={control}
            watch={watch}
            errors={errors}
            edit={true}
            setValue={setValue}
          />
        )}
      </div>
    </div>
  );
};

export default EditLabPartner;
